import { html, useState } from '/vendor/htm-preact.module.js';
import { useRead } from '/read.js';
import { KeepPage } from '/shortcuts.js';
import { bySeason, seasonLabel, seasonId } from '/seasons.js';

/**
 * One team's page inside a league: who plays for it, what it rates, and
 * this league's fixtures it has been in.
 *
 * `/api/leagues/{league_id}/teams/{circle_id}` (see cueforge/api.py's
 * `league_team`) already carries the whole shape this screen draws -- this
 * only has to lay it out, the same bargain player.js already made for a
 * player's own page.
 *
 * Reached only from a circle a league's own data produced -- a row on
 * Standings, once it names which circle it is -- never typed, so a circle
 * id matching nobody entered here is a broken link rather than a real
 * absence. The API 404s it, and this screen's error state is the same
 * plain server error every other screen here shows for one.
 */
export function TeamScreen({ leagueId, circleId, onGo, onDone }) {
  const [team, error] = useRead(
    `/api/leagues/${encodeURIComponent(leagueId)}`
    + `/teams/${encodeURIComponent(circleId)}`);

  if (error) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="team-error">
          ${error.detail || error.message}
        </p>
      </div>`;
  }
  if (team === undefined) return html`<p class="sub">Loading…</p>`;

  return html`
    <div class="screen" data-testid="team-screen">
      <div class="rowbetween">
        <h2 data-testid="team-name">${team.name}</h2>
        ${/* Issue #77. Wrapped WITH Back rather than dropped in beside it:
              `.rowbetween` is space-between and expects two children, so a
              third would space the heading, the star and Back evenly across
              the row and pull the team's name off the left edge. */''}
        <span class="row" style="width:auto">
          <${KeepPage} variant="plain" />
          <button class="secondary" style="width:auto"
                  data-testid="team-back" onClick=${onDone}>Back</button>
        </span>
      </div>
      <${TeamInLeague} team=${team} leagueId=${leagueId} onGo=${onGo} />
    </div>`;
}

/**
 * What a team IS in its league: where it plays, what it rates, who is on
 * it and what it has played.
 *
 * Split out of the screen above so the circle's own page can show the same
 * thing (GitHub issue #19). A team's page there used to offer one button,
 * to a history of FRIENDLIES played before the circle became a team —
 * which is the one set of results a team is not about. Rendering it from
 * one place rather than two means the two cannot drift, which for a roster
 * with per-player stats is the difference that would actually get noticed.
 */
export function TeamInLeague({ team, leagueId, onGo }) {
  return html`
    <div class="stack" data-testid="team-in-league">
      ${/* `None`, not "" -- a circle with no venue says nothing rather
            than "playing out of —", which "" would leave this screen to
            invent. See cueforge/api.py's `league_team`. */''}
      ${team.venue && html`
        <p class="sub" data-testid="team-venue">
          ${`Playing out of ${team.venue}`}
        </p>`}

      <div class="stack" data-testid="team-rating">
        <label>Rating</label>
        <div class="card rowbetween">
          <span>Combined</span>
          <span class="mono pts" data-testid="team-rating-combined">
            ${elo(team.rating.combined)}
          </span>
        </div>
        <div class="rowbetween">
          <span class="sub">Singles</span>
          <span class="mono sub" data-testid="team-rating-singles">
            ${elo(team.rating.singles)}
          </span>
        </div>
        <div class="rowbetween">
          <span class="sub">Doubles</span>
          <span class="mono sub" data-testid="team-rating-doubles">
            ${elo(team.rating.doubles)}
          </span>
        </div>
      </div>

      <p class="sub" data-testid="team-players">
        ${`${team.players} `}
        ${team.players === 1 ? 'player' : 'players'} in this team
      </p>

      ${/* Rendered only when the payload carries it -- a signed-in
            non-member gets `null` (see `league_team`'s `your_place`) and
            must see no section at all, not an empty one. */''}
      ${team.your_place && html`
        <p class="sub" data-testid="team-your-place">
          ${`Your place: ${team.your_place.position} of `}
          ${team.your_place.of}
        </p>`}

      <div class="stack" data-testid="team-roster">
        <label>Roster</label>
        ${team.roster.length === 0
          ? html`<p class="empty" data-testid="team-roster-empty">
                   Nobody on it yet.
                 </p>`
          : html`
            <div class="tablewrap">
              <table class="league" data-testid="team-roster-table">
                <thead>
                  <tr>
                    <th class="num">#</th><th>Player</th>
                    <th class="num">Frames</th><th class="num">W</th>
                    <th class="num">L</th><th class="num">Win%</th>
                    <th class="num">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  ${team.roster.map((r, i) => html`
                    <tr key=${r.user_id}
                        data-testid=${`team-roster-${r.user_id}`}>
                      <td class="num sub">${i + 1}</td>
                      <td>
                        ${/* The one control on this board that leads
                              somewhere -- a player's own page, the same
                              spot leagues.js's Stats board sends a name
                              to. `player_key`, not `user_id` -- for a
                              mirrored league they differ (see
                              cueforge/api.py's `league_team`), and the
                              player screen only resolves against the
                              league's own key. */''}
                        <button class="linky"
                                data-testid=${`team-roster-go-${r.user_id}`}
                                onClick=${() => onGo('player', leagueId,
                                                     r.player_key)}>
                          ${r.name}
                        </button>
                      </td>
                      <td class="num sub">${cell(r.games)}</td>
                      <td class="num">${cell(r.won)}</td>
                      <td class="num">${cell(r.lost)}</td>
                      <td class="num">${cell(r.win_pct)}</td>
                      <td class="num pts">${elo(r.elo)}</td>
                    </tr>`)}
                </tbody>
              </table>
            </div>`}
      </div>

      <${TeamFixtures} fixtures=${team.fixtures} leagueId=${leagueId}
                       onGo=${onGo} />
    </div>`;
}

/**
 * Fixtures, split by format -- league first, then a tab for competitions.
 *
 * The original request asked for this "league first, then tabs", not two
 * peers: League is always the default, and always the leftmost button when
 * there is a second one to show. A team with no cup fixtures gets no tab
 * bar at all -- not one tab, and not an empty second tab -- since a control
 * that can only ever be pressed one way is not a choice, it is decoration.
 * `f.kind` comes from `cueforge/api.py`'s `_fixture_row`, which defaults a
 * native league's untagged fixture to `'league'` rather than leaving a
 * third, unlabelled pile for this to have to handle.
 */
function TeamFixtures({ fixtures, leagueId, onGo }) {
  // Which season is on show. `undefined` means "whichever is current", so a
  // league opening a new season does not leave anybody pinned to the old one.
  const [season, setSeason] = useState(undefined);
  const seasons = bySeason(fixtures);
  const inSeason = seasons.find((s) => s.season === season)
    || seasons[0] || { season: null, rows: [] };

  // Results first, then the league's own fixtures, then competitions --
  // GitHub issue #20, and the order it asks for is the order somebody wants
  // them: what happened, then what is next, then the cup.
  //
  // A played fixture appears ONCE, under Results, rather than in both its
  // own list and this one. Two rows for one fixture, differing only in
  // whether they carry a score, is the kind of duplicate that reads as two
  // matches against the same team.
  //
  // Issue #31 filters all three piles to ONE season first, with the earlier
  // ones behind their own tabs above. Season, then pile within it -- the
  // other way round would give a Results tab whose meaning changed depending
  // on a control underneath it.
  const played = inSeason.rows.filter((f) => f.score);
  const upcoming = inSeason.rows.filter((f) => !f.score);
  const PILES = [
    { id: 'results', label: 'Results', rows: played },
    { id: 'league', label: 'League fixtures',
      rows: upcoming.filter((f) => f.kind !== 'cup') },
    { id: 'cup', label: 'Competitions',
      rows: upcoming.filter((f) => f.kind === 'cup') },
  ];
  // Open on the first pile that has anything in it. Landing on an empty
  // Results for a team that has not played yet would say "no fixtures" to
  // somebody whose season starts on Thursday.
  const [tab, setTab] = useState(null);
  const offered = PILES.filter((p) => p.rows.length > 0);
  const here = PILES.find((p) => p.id === tab)
    || offered[0] || PILES[0];

  return html`
    <div class="stack" data-testid="team-fixtures">
      <label>Fixtures</label>
      ${/* Issue #31. Only when there is more than one season to offer --
            the same rule the pile tabs below already follow, since a control
            that can only be pressed one way is decoration. */''}
      ${seasons.length > 1 && html`
        <nav class="rooms" data-testid="team-fixtures-seasons">
          ${seasons.map((s) => html`
            <button key=${s.season || 'unfiled'}
                    class=${inSeason.season === s.season ? '' : 'secondary'}
                    aria-current=${inSeason.season === s.season
                      ? 'page' : undefined}
                    data-testid=${`team-fixtures-season-${
                      seasonId(s.season)}`}
                    onClick=${() => setSeason(s.season)}>
              ${seasonLabel(s.season)}
            </button>`)}
        </nav>`}
      ${offered.length > 1 && html`
        <nav class="rooms" data-testid="team-fixtures-tabs">
          ${offered.map((p) => html`
            <button key=${p.id} class=${here.id === p.id ? '' : 'secondary'}
                    aria-current=${here.id === p.id ? 'page' : undefined}
                    data-testid=${`team-fixtures-tab-${p.id}`}
                    onClick=${() => setTab(p.id)}>${p.label}</button>`)}
        </nav>`}
      ${here.rows.length === 0
        ? html`<p class="empty" data-testid="team-fixtures-empty">
                 No fixtures yet.
               </p>`
        : html`
          <div class="tablewrap">
            <table class="league" data-testid=${`team-fixtures-${here.id}`}>
              <thead>
                <tr>
                  <th>Date</th><th>Fixture</th>
                  ${/* Named for the team, not "score": the two numbers are
                        frames won by each side, which is what a pool league
                        result IS. A column headed Score over "5" invites the
                        question "out of what". */''}
                  <th class="num">Home</th><th class="num">Away</th>
                </tr>
              </thead>
              <tbody>
                ${here.rows.map((f) => html`
                  <tr key=${f.fixture_id}
                      data-testid=${`team-fixture-${f.fixture_id}`}>
                    <td class="sub">${f.played_on}</td>
                    <td>
                      ${/* Stacked, not "Home v Away" on one line. Two pub
                            names and a "v" is the widest thing on the row by
                            a distance, and it pushed the frame columns off a
                            phone -- the table scrolled sideways to show
                            numbers that are the whole point of it. */''}
                      <button class="linky stacked"
                              data-testid=${`team-fixture-go-${f.fixture_id}`}
                              onClick=${() => onGo('fixture', leagueId,
                                                   f.fixture_id)}>
                        <span>${f.home.name}</span>
                        <span class="sub">${f.away.name}</span>
                      </button>
                    </td>
                    <td class="num pts">${f.score ? f.score.home : '—'}</td>
                    <td class="num pts">${f.score ? f.score.away : '—'}</td>
                  </tr>`)}
              </tbody>
            </table>
          </div>`}
    </div>`;
}


// Not "0" -- an absent number reads as a fact about a real team or a real
// person. Same rule leagues.js's Stats board and player.js both enforce for
// an unknown figure, reused here rather than a third spelling of it.
const cell = (v) => (v === null || v === undefined ? '—' : v);

/** A rating, rounded -- or the dash when there is none at all (a team or a
 * player with no frames on record, per cueforge/api.py's `weighted_rating`
 * and `_win_record`). */
const elo = (v) => cell(v === null || v === undefined ? null : Math.round(v));
